import {
    CaretSortIcon,
    CheckIcon,
    HandIcon, UpdateIcon
} from '@radix-ui/react-icons';
import { useCallback, useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import { useForm } from 'react-hook-form';
import api from '../../lib/api';
import { BunniesToMoveDatatable } from '../datatables/bunniesToMoveDatatable';
import { Button } from '../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/command";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { DropdownMenuItem } from '../ui/dropdown-menu';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import { toast } from '../ui/use-toast';

function TransferRegistrationsDialog({ fetchClass, currentClass, dropdown }) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [classesLoading, setClassesLoading] = useState(false);
    const [registrationsLoading, setRegistrationsLoading] = useState(false);
    const [comboboxOpen, setComboboxOpen] = useState(false)
    const form = useForm();
    const currentClassId = currentClass?.id;
    const currentClassName = currentClass?.name;
    const currentClassCompetitionId = currentClass?.competition?.id;
    const [isLoading, setIsLoading] = useState(false);
    const [competitionClasses, setCompetitionClasses] = useState([]);
    const [competitionClassRegistrations, setCompetitionClassRegistrations] = useState([]);
    const [selectedBunnies, setSelectedBunnies] = useState([]);
    const [bunniesError, setBunniesError] = useState([]);

    const [formData, setFormData] = useState({
        fromClassId: '',
        registrationIds: [],
    });

    const fetchClassesWithinCompetition = useCallback(async () => {
        if (isDialogOpen) {
            try {
                setClassesLoading(true);
                const response = await api.get(`https://skhrf-api.cmdigital.se/v1/competitions/information/${currentClassCompetitionId}`);
                setCompetitionClasses(response.data.classes);
            } catch (error) {
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: error?.response?.data?.error,
                });
                setIsDialogOpen(false);
            } finally {
                setClassesLoading(false);
            }
        }
    }, [currentClass, isDialogOpen]);

    useEffect(() => {
        fetchClassesWithinCompetition();
    }, [fetchClassesWithinCompetition]);

    // Submit
    const sendTransferRequest = (e) => {
        if (isLoading) return;

        e.preventDefault();
        setIsLoading(true);

        const registrationIds = selectedBunnies.map(bunny => bunny.registrationId);

        api.post(`https://skhrf-api.cmdigital.se/v1/registrations/transfer`, {
            ...formData,
            toClassId: currentClassId,
            registrationIds,
        })
            .then(response => {
                fetchClass();
                setSelectedBunnies([]);
                form.reset({
                    fromClassId: null,
                    registrationIds: [],
                });
                setIsLoading(false);
                toast({
                    title: 'Ekipage flyttades.',
                    description: `Ekipage har flyttats framgångsrikt.`,
                });
                setIsDialogOpen(false);
            })
            .catch(error => {
                setBunniesError(error.response.data.registrationIds);
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: error?.response?.data?.error,
                });
                setIsLoading(false);
            });
    };

    const fetchRegistrationsBasedOnClassId = async () => {
        try {
            setRegistrationsLoading(true);
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/competitions/class/${formData.fromClassId}?withAP=true`);
            setCompetitionClassRegistrations(response.data.registrations);
        } catch (error) {
            setCompetitionClassRegistrations([]);
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
            setIsDialogOpen(false);
        } finally {
            setRegistrationsLoading(false);
        }
    }

    useEffect(() => {
        if (formData.fromClassId === '') return
        fetchRegistrationsBasedOnClassId();
    }, [formData.fromClassId])

    const handleSelectionChange = useCallback((selected) => {
        setSelectedBunnies(selected);
    }, []);

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            {dropdown ? (
                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                    <DialogTrigger className='flex items-center w-full text-left'>
                        <HandIcon className='mr-3' /> Flytta hit ekipage
                    </DialogTrigger>
                </DropdownMenuItem>
            ) : (
                <DialogTrigger className='flex items-center w-full' asChild>
                    <Button>
                        <HandIcon className='mr-3' /> Flytta hit ekipage
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent className='max-h-dvh overflow-y-auto'>
                <DialogHeader className='text-left'>
                    <DialogTitle>Flytta hit ekipage</DialogTitle>
                    <DialogDescription>Ange ursprungsklass samt välj bland ekipage i listan för att flytta dem till <b>{currentClassName}</b>.</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={sendTransferRequest} className="flex flex-col gap-6">
                        <FormField
                            control={form.control}
                            name="fromClassId"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='fromClassId'>Klass</FormLabel>
                                    <FormControl>
                                        <Popover modal={true} open={comboboxOpen} onOpenChange={setComboboxOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    id='fromClassId'
                                                    variant="outline"
                                                    role="combobox"
                                                    className={cn(
                                                        "w-full justify-between",
                                                        !field.value && "text-muted-foreground"
                                                    )}
                                                >
                                                    {field.value
                                                        ? competitionClasses.find(
                                                            (competitionClass) => competitionClass.id === field.value
                                                        )?.name || 'Okänd klass'
                                                        : "Välj klass"}
                                                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="popover-content-width-full p-0">
                                                <Command>
                                                    <CommandInput placeholder="Sök klass..." />
                                                    <CommandList>
                                                        {classesLoading && <div className='flex justify-center items-center w-full min-h-16'><UpdateIcon className='loader-spinner size-6' /></div>}
                                                        {!classesLoading && <CommandEmpty>Ingen klass hittades.</CommandEmpty>}
                                                        <CommandGroup>
                                                            {competitionClasses
                                                                .filter((competitionClass) => competitionClass?.id !== currentClassId)
                                                                .map((competitionClass) => (
                                                                    <CommandItem
                                                                        className='cursor-pointer'
                                                                        value={competitionClass?.id}
                                                                        key={competitionClass?.id}
                                                                        onSelect={() => {
                                                                            setComboboxOpen(false);
                                                                            form.setValue("fromClassId", competitionClass?.id);
                                                                            setFormData({ ...formData, fromClassId: competitionClass?.id });
                                                                        }}
                                                                    >
                                                                        <CheckIcon
                                                                            className={cn(
                                                                                "mr-2 h-4 w-4",
                                                                                competitionClass?.id === field.value
                                                                                    ? "opacity-100"
                                                                                    : "opacity-0"
                                                                            )}
                                                                        />
                                                                        {competitionClass?.name} - Bedömning {competitionClass?.assessment}
                                                                    </CommandItem>
                                                                ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </form>
                </Form>
                <BunniesToMoveDatatable bunnies={competitionClassRegistrations} isLoading={registrationsLoading} onSelectionChange={handleSelectionChange} errors={bunniesError} originSportClassId={formData.fromClassId} />
                <div className='grid grid-cols-2 gap-3'>
                    <DialogClose asChild>
                        <Button type='button' variant='secondary'>Avbryt</Button>
                    </DialogClose>
                    <Button type="submit" disabled={isLoading} onClick={sendTransferRequest}>
                        {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Flytta ekipage</>}
                    </Button>
                </div>

            </DialogContent>

        </Dialog>
    );
};

export default TransferRegistrationsDialog;
